import React from 'react';
import { BookOpen, GraduationCap, Calendar, MapPin, AlertCircle, CheckCircle2, ChevronLeft } from 'lucide-react';
import { AppView } from '../types';

interface SchoolGuideProps { setView: (view: AppView) => void; }

const SchoolGuide: React.FC<SchoolGuideProps> = ({ setView }) => {
  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8">
      <button onClick={() => setView(AppView.GUIDES)}
        className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-indigo-600 transition mb-6">
        <ChevronLeft size={16} /> All Guides
      </button>
      {/* Hero */}
      <div className="bg-gradient-to-br from-amber-600 to-orange-500 rounded-2xl p-8 mb-8 text-white shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <BookOpen size={32} />
          <span className="text-amber-200 font-semibold text-sm uppercase tracking-wider">2026 NRI Relocation Blueprint</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-extrabold leading-tight mb-3">
          The IB/IGCSE School Admission Guide: Boards, Calendars & Mid-Year Realities
        </h1>
        <p className="text-amber-100 text-base">
          Pune schools begin admissions as early as November for the following June. Miss the window
          and your child loses a full academic year. Here is what the community learned the hard way.
        </p>
      </div>

      {/* Article Hero Visual */}
      <div className="bg-gradient-to-br from-slate-800 to-amber-900 rounded-2xl h-64 mb-8 flex items-center justify-center relative overflow-hidden shadow-lg">
        <div className="absolute -top-10 -left-10 w-48 h-48 bg-amber-500 rounded-full opacity-20 blur-2xl"></div>
        <div className="absolute -bottom-10 -right-10 w-56 h-56 bg-orange-500 rounded-full opacity-20 blur-2xl"></div>
        <div className="relative z-10 flex items-center gap-8">
          <div className="flex flex-col items-center gap-2">
            <div className="bg-white/10 border border-white/20 rounded-2xl p-5 backdrop-blur-sm">
              <GraduationCap size={40} className="text-amber-300" />
            </div>
            <span className="text-white/60 text-xs font-semibold uppercase tracking-wide">IB / IGCSE</span>
          </div>
          <div className="text-white/30 text-3xl font-thin">vs</div>
          <div className="flex flex-col items-center gap-2">
            <div className="bg-white/10 border border-white/20 rounded-2xl p-5 backdrop-blur-sm">
              <Calendar size={40} className="text-orange-300" />
            </div>
            <span className="text-white/60 text-xs font-semibold uppercase tracking-wide">CBSE</span>
          </div>
          <div className="text-white/30 text-3xl font-thin">+</div>
          <div className="flex flex-col items-center gap-2">
            <div className="bg-white/10 border border-white/20 rounded-2xl p-5 backdrop-blur-sm">
              <MapPin size={40} className="text-yellow-300" />
            </div>
            <span className="text-white/60 text-xs font-semibold uppercase tracking-wide">Pune Zones</span>
          </div>
        </div>
        <div className="absolute bottom-4 left-0 right-0 text-center">
          <span className="text-white/40 text-xs font-medium tracking-widest uppercase">Find the Right School Before You Land</span>
        </div>
      </div>

      {/* Intro */}
      <div className="mb-8">
        <p className="text-slate-600 text-base leading-relaxed">
          For NRI families, school selection is often the most emotionally charged decision of the entire
          relocation. Your child's academic continuity, social integration, and mental health all hinge on
          choosing the right board and school. The good news: Pune has a genuinely strong international
          school ecosystem. The challenge is navigating a system that was not designed with returning NRIs in mind.
        </p>
      </div>

      {/* Part 1: Which Board */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-amber-100 p-2 rounded-lg">
            <GraduationCap size={22} className="text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Part 1: IB vs IGCSE vs CBSE — Which Board for Returning NRI Kids?</h2>
        </div>
        <p className="text-slate-600 mb-5">
          The community answer is clear: for children in Grade 6 and above returning from the US, UK, or
          Germany, IB or IGCSE is the recommended path. CBSE is workable for younger children or those
          with strong Hindi. The key factors are language, teaching style, and peer group.
        </p>

        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm mb-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-amber-600 text-white">
                <th className="text-left p-4 font-semibold">Factor</th>
                <th className="text-left p-4 font-semibold">IB / IGCSE</th>
                <th className="text-left p-4 font-semibold">CBSE</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white border-b border-slate-100">
                <td className="p-4 font-semibold text-slate-800 align-top">Hindi Requirement</td>
                <td className="p-4 text-slate-600 align-top"><span className="inline-block bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded mb-1">No mandatory Hindi</span><p>Can substitute Spanish, French, or other languages.</p></td>
                <td className="p-4 text-slate-600 align-top"><span className="inline-block bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded mb-1">Hindi compulsory up to Grade 8</span><p>Major friction point for kids raised abroad.</p></td>
              </tr>
              <tr className="bg-slate-50 border-b border-slate-100">
                <td className="p-4 font-semibold text-slate-800 align-top">Teaching Style</td>
                <td className="p-4 text-slate-600 align-top">Inquiry-based, project-oriented. Very similar to US/UK schools. Easier cognitive transition.</td>
                <td className="p-4 text-slate-600 align-top">Rote-learning heavy, exam-focused. Significant adjustment for US-educated kids.</td>
              </tr>
              <tr className="bg-white border-b border-slate-100">
                <td className="p-4 font-semibold text-slate-800 align-top">Annual Fees (Pune)</td>
                <td className="p-4 text-slate-600 align-top">₹3–8 lakhs/year depending on school and grade.</td>
                <td className="p-4 text-slate-600 align-top">₹1–3 lakhs/year. More affordable.</td>
              </tr>
              <tr className="bg-slate-50">
                <td className="p-4 font-semibold text-slate-800 align-top">NRI Peer Group</td>
                <td className="p-4 text-slate-600 align-top">High concentration of returnee families. Your child will find others in the same situation.</td>
                <td className="p-4 text-slate-600 align-top">Mostly local students. Social integration takes longer for returnee kids.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex gap-3">
            <AlertCircle size={18} className="text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-bold text-amber-800 text-sm">Community Verdict</p>
              <p className="text-amber-700 text-sm">
                <em>"Better to choose IB if returning from USA in secondary school."</em> For younger children
                (Grades 1–4), CBSE is a viable option if the school has an NRI-friendly culture and the family
                plans to stay long-term. For older kids, do not compromise on board alignment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Part 2: Admission Calendar */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-amber-100 p-2 rounded-lg">
            <Calendar size={22} className="text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Part 2: The Pune School Admission Calendar</h2>
        </div>
        <p className="text-slate-600 mb-5">
          This is where most NRI families get blindsided. Indian school admissions do not work like US
          rolling admissions. There are hard windows, and missing them means a full-year wait or
          difficult mid-year scrambles.
        </p>

        <div className="space-y-3 mb-5">
          {[
            {
              month: 'Oct – Dec',
              color: 'bg-amber-100 text-amber-800 border-amber-200',
              event: 'Admission forms open for the following academic year (June start)',
              detail: 'Most CBSE and IGCSE schools open their forms now. This is when to visit campuses, shortlist, and submit applications for a June admission.',
            },
            {
              month: 'Jan – Feb',
              color: 'bg-orange-100 text-orange-800 border-orange-200',
              event: 'Entrance tests and interviews for competitive CBSE schools',
              detail: 'Popular CBSE schools hold written assessments and parent interviews. Spots fill up fast — do not delay past December.',
            },
            {
              month: 'Mar – Apr',
              color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
              event: 'IB school forms open for August-start intake',
              detail: 'IB schools like Universal Wisdom and Symbiosis International start in August, aligned with the US school year. Their admission forms open March–April.',
            },
            {
              month: 'Jun – Aug',
              color: 'bg-blue-100 text-blue-800 border-blue-200',
              event: 'New academic year begins (June for CBSE/IGCSE, August for IB)',
              detail: 'This is also when IB schools are most flexible about mid-year or late admissions for incoming NRI families.',
            },
          ].map((item, i) => (
            <div key={i} className={`flex gap-4 items-start p-4 rounded-xl border ${item.color}`}>
              <div className={`text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full bg-white/60 shrink-0 mt-0.5`}>
                {item.month}
              </div>
              <div>
                <p className="font-semibold text-sm mb-1">{item.event}</p>
                <p className="text-xs opacity-80 leading-relaxed">{item.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Part 3: Mid-Year Admissions */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-amber-100 p-2 rounded-lg">
            <CheckCircle2 size={22} className="text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Part 3: Mid-Year Admissions — Which Schools Say Yes</h2>
        </div>
        <p className="text-slate-600 mb-4">
          If you cannot align your move date with the June or August admission window, mid-year admission
          is your only option. The rules differ dramatically by board. Community insight:
          <em className="text-slate-500"> "Not for international schools — the assumption is that people coming
          from abroad will come when and whenever."</em>
        </p>

        <div className="grid md:grid-cols-2 gap-4 mb-5">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">Generally Accepts</span>
            </div>
            <h3 className="font-bold text-slate-800 mb-3">IB & IGCSE Schools</h3>
            <ul className="space-y-2">
              {['Universal Wisdom (IB)', 'Symbiosis International (IB)', 'VIBGYOR High (IGCSE)', 'Billabong (IGCSE)', 'CNS — Cambridge (IGCSE)'].map((s) => (
                <li key={s} className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle2 size={14} className="text-green-500 shrink-0" /> {s}
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-400 mt-3 italic">Subject to seat availability per grade</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">Case-by-Case</span>
            </div>
            <h3 className="font-bold text-slate-800 mb-3">CBSE Schools</h3>
            <ul className="space-y-2">
              {['Sanskriti School (Kharadi)', 'Narayana (Multiple)', 'HDFC School (Amanora)', 'GIS (Hadapsar)', 'Kalyani (Amanora)'].map((s) => (
                <li key={s} className="flex items-center gap-2 text-sm text-slate-600">
                  <AlertCircle size={14} className="text-amber-500 shrink-0" /> {s}
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-400 mt-3 italic">Call ahead — mid-year policy varies by principal</p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-blue-800 text-sm">
            <strong>Practical tip:</strong> When calling schools, mention you are an NRI family relocating from
            abroad. Most school offices treat this as a separate track from domestic transfers and are
            considerably more accommodating. Always ask for the <strong>Principal's office</strong> directly,
            not the admissions desk.
          </p>
        </div>
      </section>

      {/* Part 4: Area-wise schools */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-amber-100 p-2 rounded-lg">
            <MapPin size={22} className="text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Part 4: Area-wise School Recommendations</h2>
        </div>
        <p className="text-slate-600 mb-5">
          School selection and neighborhood selection go hand-in-hand. Most families shortlist 2–3 schools
          first, then find housing within commute range. Here is the community's area-wise summary.
        </p>

        <div className="space-y-4">
          {[
            {
              zone: 'Kharadi / Amanora / Magarpatta',
              color: 'border-l-indigo-400',
              schools: 'CNS (Cambridge), HDFC School (CBSE), VIBGYOR (IGCSE), Billabong (IGCSE), GIS, Kalyani, Narayana',
              note: 'Highest concentration of NRI families. Strong community support for new arrivals. Amanora Township schools are walkable from society.'
            },
            {
              zone: 'Baner / Balewadi / Wakad (Hinjewadi side)',
              color: 'border-l-emerald-400',
              schools: 'Symbiosis International (IB), Delhi Public School, Indus International, Victorious Kidss Educares (IB)',
              note: 'Good for Hinjewadi office commuters. IB options are strong here. Less NRI density than East Pune but growing fast.'
            },
            {
              zone: 'Viman Nagar / Kalyani Nagar',
              color: 'border-l-amber-400',
              schools: 'Universal Wisdom (IB — August start), Euro School, Kothari International',
              note: 'Universal Wisdom\'s August start is a major advantage for US-returning families. Aligns with US academic calendar perfectly.'
            },
          ].map((zone, i) => (
            <div key={i} className={`bg-white border border-slate-200 border-l-4 ${zone.color} rounded-xl p-5 shadow-sm`}>
              <h3 className="font-bold text-slate-800 mb-2">{zone.zone}</h3>
              <p className="text-sm text-slate-600 mb-2"><span className="font-semibold text-slate-700">Schools:</span> {zone.schools}</p>
              <p className="text-xs text-slate-500 italic">{zone.note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Signature */}
      <div className="border-t border-slate-200 pt-6 mt-8 text-center">
        <p className="text-slate-400 text-xs uppercase tracking-widest font-semibold">Published by</p>
        <p className="text-indigo-700 font-extrabold text-lg tracking-wide mt-1">NRIGUIDE.BHARAT</p>
      </div>
    </div>
  );
};

export default SchoolGuide;
